// Responsive-layout hook for the v2 chrome.
//
// We treat the app as having three tiers, mapped to MUI breakpoints:
//
//   - mobile  → only `xs` (<sm  = < 600px). Phones.
//   - tablet  → `sm` OR `md`  (600–1199px). Small laptops and tablets.
//   - desktop → `lg` and up  (≥ 1200px). The default desktop chrome.
//
// The breakpoint split is what the user signed off on: phones get the
// hamburger + bottom strip treatment; tablets keep the drawer but
// upgrade the chat surface to the rail-mode RunProgressPanelV2; full
// desktop keeps the existing three-pane layouts unchanged.
//
// Returns three flags rather than a single string so consumers can
// write `if (mobile)` or `if (mobile || tablet)` without unwrapping a
// union — the latter combination ("anything but desktop") is the most
// common one (drawer-driven nav applies to both).
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

export interface ResponsiveLayout {
    /** xs only — phones. */
    mobile: boolean;
    /** sm or md — tablets / small laptops. */
    tablet: boolean;
    /** lg and up — desktop chrome. */
    desktop: boolean;
}

export function useResponsiveLayout(): ResponsiveLayout {
    const theme = useTheme();
    // `down('sm')` matches the half-open interval [0, sm), i.e. xs only.
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    // `between('sm', 'lg')` matches [sm, lg) = sm + md.
    const tablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
    const desktop = useMediaQuery(theme.breakpoints.up('lg'));
    return { mobile, tablet, desktop };
}
